/**
 * Welle-7S.3 — Mehr-Laden-Button als Vanilla Custom-Element.
 *
 * Pattern: Click → fetch(?page=N&section_id=...) → DOMParser → append <li>
 * an existing #product-grid > ul.grid. Spinner während Loading. AbortController
 * cancelt pending Fetches bei Filter-Änderung. MutationObserver auf
 * #ProductGridContainer (childList: true) detektiert facets.js innerHTML-Replace
 * und triggert Abort. disconnectedCallback als Fallback-Insurance.
 *
 * Kein facets.js-Edit. Refresh-Standard-Code bleibt unangetastet.
 */
class LoadMoreButton extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.mutationObserver = null;
  }

  connectedCallback() {
    this.button = this.querySelector('button.welle7s-load-more');
    this.label = this.querySelector('.welle7s-load-more__label');
    this.hint = this.querySelector('.welle7s-load-more__hint');
    if (!this.button) return;
    this.button.addEventListener('click', this.onClick.bind(this));
    this.setupFilterRaceObserver();

    // Scrollposition laufend mitschreiben, solange nachgeladen wurde —
    // sonst kennen wir sie beim Zurueckkommen nicht.
    this.onScroll = () => {
      if (this.geladeneSeiten > 0) this.zustandMerken();
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });

    this.zustandLaden();
  }

  /** Schluessel pro Kategorie/Filterstand, damit sich Seiten nicht mischen. */
  get zustandSchluessel() {
    return 'welle7s-mehr:' + location.pathname + location.search;
  }

  zustandMerken() {
    try {
      sessionStorage.setItem(this.zustandSchluessel, JSON.stringify({
        seiten: this.geladeneSeiten || 0,
        scrollY: Math.round(window.scrollY),
      }));
    } catch (e) { /* privates Fenster o. ae. — dann eben ohne */ }
  }

  /**
   * Beim Zurueckkommen die zuvor nachgeladenen Seiten erneut anhaengen und
   * danach an die alte Stelle scrollen. Laeuft nur, wenn der Browser die
   * Seite wirklich neu aufbaut; bei bfcache bleibt das DOM ohnehin erhalten.
   */
  async zustandLaden() {
    let zustand;
    try {
      zustand = JSON.parse(sessionStorage.getItem(this.zustandSchluessel) || 'null');
    } catch (e) { return; }
    if (!zustand || !(zustand.seiten > 0)) return;

    // Wer die Kategorie frisch aufruft, soll nicht ungefragt 100 Artikel
    // bekommen. Nur nach echter Rueckkehr wiederherstellen.
    const nav = performance.getEntriesByType('navigation')[0];
    const zurueck = nav && (nav.type === 'back_forward' || nav.type === 'reload');
    if (!zurueck) {
      try { sessionStorage.removeItem(this.zustandSchluessel); } catch (e) {}
      return;
    }

    for (let i = 0; i < zustand.seiten; i++) {
      if (!this.isConnected || !this.dataset.nextUrl) break;
      await this.nachladen();
    }
    if (zustand.scrollY > 0) {
      requestAnimationFrame(() => window.scrollTo(0, zustand.scrollY));
    }
  }

  disconnectedCallback() {
    this.abort();
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      this.onScroll = null;
    }
  }

  /**
   * MutationObserver auf #ProductGridContainer (childList ohne subtree).
   * facets.js macht ProductGridContainer.innerHTML = ... bei Filter-Change
   * → triggert childList-Mutation auf direktem Kind-Level → Abort.
   * Eigene Card-Appends gehen 2-3 Levels tiefer (#product-grid > ul > li),
   * triggern KEINE childList-Mutation auf ProductGridContainer-Ebene → kein
   * False-Positive.
   */
  setupFilterRaceObserver() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    this.mutationObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0)) {
          this.abort();
          break;
        }
      }
    });
    this.mutationObserver.observe(container, { childList: true });
  }

  abort() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  async onClick(event) {
    event.preventDefault();
    await this.nachladen();
  }

  /**
   * Eine Seite anhaengen. Wird vom Klick genutzt und beim Wiederherstellen
   * nach dem Zurueckspringen — deshalb ohne Event-Bezug.
   */
  async nachladen() {
    if (!this.button || this.button.classList.contains('loading')) return;
    if (!this.dataset.nextUrl) return;

    this.setLoadingState(true);
    this.abortController = new AbortController();

    const baseUrl = this.dataset.nextUrl;
    const sectionId = this.dataset.sectionId;
    const sep = baseUrl.includes('?') ? '&' : '?';
    const url = `${baseUrl}${sep}section_id=${encodeURIComponent(sectionId)}`;

    try {
      const res = await fetch(url, { signal: this.abortController.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const dom = new DOMParser().parseFromString(html, 'text/html');

      // Das Raster finden. Dawn-Standard ist ein Container #product-grid mit
      // einer <ul class="grid"> darin — in diesem Theme IST #product-grid aber
      // die <ul> selbst (sections/main-collection-product-grid.liquid).
      // Der alte Selektor '#product-grid > ul.grid' konnte deshalb nie
      // greifen, der Button lief immer in den Fehlerzweig.
      const findeRaster = (root) => {
        const pg = root.querySelector('#product-grid');
        if (!pg) return null;
        return pg.matches('ul') ? pg : pg.querySelector('ul.grid');
      };

      const neuesRaster = findeRaster(dom);
      const grid = findeRaster(document);
      const newCards = neuesRaster ? neuesRaster.querySelectorAll(':scope > li') : [];
      if (!grid || newCards.length === 0) {
        throw new Error('Kein neues Grid-HTML gefunden');
      }

      const fragment = document.createDocumentFragment();
      newCards.forEach((card) => fragment.appendChild(card));
      grid.appendChild(fragment);

      // Range-Counter "Zeige X–Y von Z" aktualisieren
      const newRange = dom.querySelector('.welle7s-pagination-range');
      const currentRange = document.querySelector('.welle7s-pagination-range');
      if (newRange && currentRange) {
        currentRange.innerHTML = newRange.innerHTML;
      }

      // Button: nächste Page-URL übernehmen oder selbst entfernen
      const newLoadMore = dom.querySelector('load-more-button');
      if (newLoadMore) {
        this.dataset.nextUrl = newLoadMore.dataset.nextUrl;
        const newHint = newLoadMore.querySelector('.welle7s-load-more__hint');
        if (newHint && this.hint) {
          this.hint.textContent = newHint.textContent;
        }
      } else {
        this.remove();
        return;
      }

      // Kein history.replaceState mehr auf die Seiten-URL.
      // Vorher wurde die Adresse auf ?page=2 umgeschrieben. Wer danach einen
      // Artikel oeffnete und zurueckging, landete auf ?page=2 — dort rendert
      // Shopify NUR die zweite Seite, die ersten Artikel fehlten, und die vom
      // Browser wiederhergestellte Scrollposition lag hinter dem Inhalt: man
      // stand im Footer vor einem halbleeren Raster.
      // Stattdessen merken wir uns den Zustand und stellen ihn beim
      // Zurueckkommen selbst wieder her (siehe zustandMerken/zustandLaden).
      this.geladeneSeiten = (this.geladeneSeiten || 0) + 1;
      this.zustandMerken();

      // Custom-Event für Lazy-Image-Reinit oder andere Komponenten
      document.dispatchEvent(new CustomEvent('welle7s:load-more-appended', {
        detail: { count: newCards.length },
      }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[Welle-7S.3] Mehr-Laden fehlgeschlagen:', err);
      this.setErrorState();
    } finally {
      this.abortController = null;
      this.setLoadingState(false);
    }
  }

  setLoadingState(loading) {
    this.button.classList.toggle('loading', loading);
    this.button.disabled = loading;
    this.button.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  setErrorState() {
    this.button.classList.add('error');
    if (this.label) this.label.textContent = 'Erneut versuchen';
    if (this.hint) this.hint.style.display = 'none';
  }
}

if (!customElements.get('load-more-button')) {
  customElements.define('load-more-button', LoadMoreButton);
}
